/**
 * Type: every word on a public page, set from the scale in the brand's faces.
 *
 * Two faces and no more. **Headings are the brand's display face** (`primary` in the record — the
 * loud one, the reason a brand picks a typeface) and **everything else is its body face**. A size is a
 * step on the scale (`hero`, `h1`, `h2`, `h3`, `lead`, `body`, `small`, `micro`), never a number, and
 * the display steps carry their own tight leading and negative tracking, because a condensed poster
 * face set at body leading is not a headline.
 *
 * A heading's LEVEL is semantics and its SIZE is presentation, and they are separate props: a product
 * page's title is an `<h1>` at the `h1` step, the shop's hero headline is an `<h1>` at `hero`, and a
 * band's section title is an `<h2>` that may well be set at `h3`.
 */
import type { ReactNode } from 'react';

import { cx, mod } from './cx';
import type { Align } from './layout';

export type Size = 'hero' | 'h1' | 'h2' | 'h3' | 'lead' | 'body' | 'small' | 'micro';
export type Tone = 'ink' | 'muted' | 'accent' | 'accent-2' | 'danger' | 'positive' | 'inherit';

const DEFAULT_SIZE: Record<1 | 2 | 3, Size> = { 1: 'h1', 2: 'h2', 3: 'h3' };

/**
 * A heading, optionally two-tone: `accent` continues the sentence in the brand's second colour —
 * "Questions, *answered*" — rather than interrupting it.
 *
 * `caps` sets it as a section label (letterspaced capitals), the heading over a collection.
 */
export function Heading({
  children,
  level = 2,
  size,
  accent,
  align,
  caps,
  font = 'display',
}: {
  children?: ReactNode | undefined;
  level?: 1 | 2 | 3 | undefined;
  size?: Size | undefined;
  accent?: ReactNode | undefined;
  align?: Align | undefined;
  caps?: boolean | undefined;
  font?: 'display' | 'body' | undefined;
}) {
  const Tag = level === 1 ? 'h1' : level === 3 ? 'h3' : 'h2';
  return (
    <Tag
      className={cx(
        'a-heading',
        mod('a-size', 'is', size ?? DEFAULT_SIZE[level]),
        mod('a-align', 'is', align),
        mod('a-font', 'is', font),
        caps && 'a-caps',
      )}
    >
      {children}
      {accent ? <span className="a-heading__accent"> {accent}</span> : null}
    </Tag>
  );
}

/**
 * Words. A paragraph by default; `inline` for a run inside another line.
 *
 * `measure` caps the line in characters — a paragraph set across 1180px is a paragraph nobody
 * finishes. `clamp` cuts at one or two lines with an ellipsis, for a card whose neighbours must keep
 * their baselines. `numeric` sets tabular figures, so a column of prices lines up.
 */
export function Text({
  children,
  size = 'body',
  tone = 'ink',
  weight,
  align,
  font,
  inline,
  measure,
  clamp,
  strike,
  numeric,
  caps,
  alert,
  title,
  id,
}: {
  children?: ReactNode | undefined;
  size?: Size | undefined;
  tone?: Tone | undefined;
  weight?: 'regular' | 'medium' | 'bold' | undefined;
  align?: Align | undefined;
  font?: 'display' | 'body' | 'mono' | undefined;
  inline?: boolean | undefined;
  measure?: 'measure' | 'tight' | undefined;
  clamp?: 1 | 2 | undefined;
  strike?: boolean | undefined;
  numeric?: boolean | undefined;
  caps?: boolean | undefined;
  /** Announce it the moment it appears — an error a screen reader must not have to go looking for. */
  alert?: boolean | undefined;
  /** The full text, for a line that has been cut. */
  title?: string | undefined;
  id?: string | undefined;
}) {
  const Tag = inline ? 'span' : 'p';
  return (
    <Tag
      id={id}
      title={title}
      {...(alert ? { role: 'alert' } : {})}
      className={cx(
        'a-text',
        mod('a-size', 'is', size),
        mod('a-tone', 'is', tone),
        mod('a-weight', 'is', weight),
        mod('a-align', 'is', align),
        mod('a-font', 'is', font),
        mod('a-measure', 'is', measure),
        mod('a-clamp', 'is', clamp),
        strike && 'a-strike',
        numeric && 'a-numeric',
        caps && 'a-caps',
      )}
    >
      {children}
    </Tag>
  );
}

/**
 * The small letterspaced line above a heading — the brand's tagline over a hero, a section's name
 * over its title. `pill` outlines it.
 */
export function Eyebrow({
  children,
  pill,
  tone = 'accent-2',
  title,
}: {
  children?: ReactNode | undefined;
  pill?: boolean | undefined;
  tone?: 'accent-2' | 'muted' | undefined;
  title?: string | undefined;
}) {
  return (
    <p
      title={title}
      className={cx('a-eyebrow', pill && 'a-eyebrow--pill', mod('a-tone', 'is', tone))}
    >
      {children}
    </p>
  );
}

/** A list with a marker per row — problems, gains, what a product includes. */
export function Bullets({
  items,
  marker = 'dot',
  tone = 'accent',
}: {
  items: readonly string[];
  marker?: 'dot' | 'check' | undefined;
  tone?: 'accent' | 'positive' | 'danger' | undefined;
}) {
  return (
    <ul className="a-bullets">
      {items.map((item) => (
        <li key={item} className="a-bullets__item">
          <span aria-hidden="true" className={cx('a-bullets__marker', mod('a-tone', 'is', tone))}>
            {marker === 'check' ? '✓' : '·'}
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

/** A ledger of included things: each on its own ruled line, with a tick at the end. */
export function Checklist({ items }: { items: readonly string[] }) {
  return (
    <ul className="a-checklist">
      {items.map((item) => (
        <li key={item} className="a-checklist__item">
          <span>{item}</span>
          <span aria-hidden="true" className="a-checklist__tick">
            ✓
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * One numbered thing in a grid of them — "what you get". Numbered because four identical boxes are
 * four identical boxes; the number tells the reader how much there is.
 */
export function Feature({ index, children }: { index: number; children?: ReactNode }) {
  return (
    <div className="a-feature">
      <span aria-hidden="true" className="a-feature__index">
        {String(index).padStart(2, '0')}
      </span>
      <span>{children}</span>
    </div>
  );
}

/** A testimonial: the quote, then who said it. Never invented — real ones or none. */
export function Quote({
  children,
  author,
  role,
}: {
  children?: ReactNode | undefined;
  author: string;
  role?: string | null | undefined;
}) {
  return (
    <figure className="a-quote">
      <blockquote className="a-quote__body">“{children}”</blockquote>
      <figcaption className="a-quote__by">
        <b>{author}</b>
        {role ? ` · ${role}` : ''}
      </figcaption>
    </figure>
  );
}

/**
 * Questions and answers, each one folded. A native `<details>`, so it opens from a keyboard and
 * works with JavaScript off; the chevron turns with `open`, because with the browser's own marker
 * hidden nothing else on the row says it opens.
 */
export function Faq({ items }: { items: ReadonlyArray<{ question: string; answer: string }> }) {
  return (
    <div className="a-faq">
      {items.map((item) => (
        <details key={item.question} className="a-faq__item">
          <summary className="a-faq__question">
            <span>{item.question}</span>
            <svg viewBox="0 0 24 24" aria-hidden="true" className="a-faq__chevron">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </summary>
          <p className="a-faq__answer">{item.answer}</p>
        </details>
      ))}
    </div>
  );
}
